import { useContext, useEffect, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import isProd from '@utils/isProd';

import TMSProvider from 'components/TMSProvider';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import {
  LoadBuildingGuardrailProvider,
  useLoadBuildingGuardrail,
} from 'hooks/useLoadBuildingGuardrail';
import useLogPostHogPageView from 'hooks/useLogPostHogPageView';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { recordSuggestionOpened } from 'lib/api/recordSuggestionOpened';
import { getMcleodIntegrationFromList } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/helpers';
import { Maybe } from 'types/UtilityTypes';
import { TMS, integrationNameMap } from 'types/enums/Integrations';
import Pageview from 'types/enums/Pageview';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import captureException from 'utils/captureException';

import UnifiedAljexLoadForm from './AljexSectionForms/UnifiedAljexLoadForm';
import UnifiedGlobalTranzForm from './GlobalTranzSectionForms/UnifiedGlobalTranzForm';
import UnifiedMercuryGateBoomiLoadForm from './MercuryGateBoomiSectionForms/UnifiedMercuryGateBoomiLoadForm';
import UnifiedPlsLoadForm from './PlsSectionForms/UnifiedPlsLoadForm';
import { ProdTMSWarningBanner } from './ProdTMSGuardrail';
import UnifiedRevenovaLoadForm from './RevenovaSectionForms/UnifiedRevenovaLoadForm';
import UnifiedStarkLoadForm from './StarkSectionForms/UnifiedStarkLoadForm';
import UnifiedTaiLoadForm from './TaiSectionForms/UnifiedTaiLoadForm';
import UnifiedThreeGLoadForm from './ThreeGSectionForms/UnifiedThreeGLoadForm';
import TurvoLoadForm from './TurvoLoadForm';
import UnifiedMcLeodLoadForm from './UnifiedMcLeodLoadForm';
import { BuiltLoadsNotice } from './components/BuiltLoadsNotice';
import { shouldRecordLoadBuildingSuggestionOpened } from './utils/shouldRecordLoadBuildingSuggestionOpened';

type LoadBuildingTmsFlags = {
  isMcleodEnterpriseTMS: Maybe<boolean>;
  isTurvoTMS: Maybe<boolean>;
  isTaiTMS: Maybe<boolean>;
  isRevenovaTMS: Maybe<boolean>;
  isAljexTMS: Maybe<boolean>;
  isGlobalTranzTMS: Maybe<boolean>;
  isThreeGTMS: Maybe<boolean>;
  isStarkTMS: Maybe<boolean>;
  isMercurygateBoomiTMS: Maybe<boolean>;
  isPlsTMS: Maybe<boolean>;
};

function isLoadBuildingFormReady(flags: LoadBuildingTmsFlags): boolean {
  return (
    flags.isMcleodEnterpriseTMS === true ||
    flags.isTurvoTMS === true ||
    flags.isTaiTMS === true ||
    flags.isRevenovaTMS === true ||
    flags.isAljexTMS === true ||
    flags.isGlobalTranzTMS === true ||
    flags.isThreeGTMS === true ||
    flags.isStarkTMS === true ||
    flags.isMercurygateBoomiTMS === true ||
    flags.isPlsTMS === true
  );
}

type LoadBuildingTabProps = {
  // The thread's load building cards whose load is already built (handledState built).
  builtSuggestions?: LoadBuildingSuggestions[];
  onOpenBuiltLoad?: (loadIdentifier: string) => void;
};

const NO_BUILT_SUGGESTIONS: LoadBuildingSuggestions[] = [];

export default function LoadBuildingTab({
  builtSuggestions = NO_BUILT_SUGGESTIONS,
  onOpenBuiltLoad,
}: LoadBuildingTabProps) {
  const serviceFeatures = useServiceFeatures();
  const serviceID = serviceFeatures.serviceID;
  const isServiceLoading = serviceFeatures.isLoading;
  const tmsIntegrations = serviceFeatures.tmsIntegrations;
  const [isMcleodEnterpriseTMS, setIsMcleodEnterpriseTMS] =
    useState<Maybe<boolean>>(null);
  const [isTurvoTMS, setIsTurvoTMS] = useState<Maybe<boolean>>(null);
  const [isTaiTMS, setIsTaiTMS] = useState<Maybe<boolean>>(null);
  const [isRevenovaTMS, setIsRevenovaTMS] = useState<Maybe<boolean>>(null);
  const [isAljexTMS, setIsAljexTMS] = useState<Maybe<boolean>>(null);
  const [isGlobalTranzTMS, setIsGlobalTranzTMS] =
    useState<Maybe<boolean>>(null);
  const [isThreeGTMS, setIsThreeGTMS] = useState<Maybe<boolean>>(null);
  const [isStarkTMS, setIsStarkTMS] = useState<Maybe<boolean>>(null);
  const [isMercurygateBoomiTMS, setIsMercurygateBoomiTMS] =
    useState<Maybe<boolean>>(null);
  const [isPlsTMS, setIsPlsTMS] = useState<Maybe<boolean>>(null);
  // The built cards the rep chose "Build another load" for. Keyed by their IDs, so a thread with
  // other built cards shows the notice again.
  const [buildAnotherForKey, setBuildAnotherForKey] =
    useState<Maybe<string>>(null);

  useLogPostHogPageView(Pageview.LoadBuilding, {
    form_type: (() => {
      switch (true) {
        case isMcleodEnterpriseTMS:
          return TMS.Mcleod;
        case isTurvoTMS:
          return TMS.Turvo;
        case isTaiTMS:
          return TMS.Tai;
        case isRevenovaTMS:
          return TMS.Revenova;
        case isAljexTMS:
          return TMS.Aljex;
        case isGlobalTranzTMS:
          return TMS.GlobalTranzTMS;
        case isThreeGTMS:
          return TMS.ThreeG;
        case isStarkTMS:
          return TMS.Stark;
        case isMercurygateBoomiTMS:
          return TMS.MercurygateBoomi;
        case isPlsTMS:
          return TMS.PLS;
        default:
          return null;
      }
    })(),
    tms_integrations_count: tmsIntegrations?.length,
    service_id: serviceID,
  });

  const {
    currentState: {
      clickedSuggestion,
      curSuggestionList,
      goToSuggestion,
      shouldKitAutoSelect,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);

  useEffect(() => {
    // Prefer a pending LB card when auto-selecting
    const firstPendingLb = curSuggestionList.find(
      (s) =>
        s.pipeline === SuggestionPipelines.LoadBuilding &&
        s.status !== SuggestionStatus.Accepted
    );
    const firstLb =
      firstPendingLb ??
      curSuggestionList.find(
        (s) => s.pipeline === SuggestionPipelines.LoadBuilding
      );

    if (!firstLb) {
      if (clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding) {
        setCurrentState((prevState) => ({
          ...prevState,
          clickedSuggestion: null,
        }));
      }
      return;
    }

    const clickedStillInList =
      clickedSuggestion != null &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding &&
      curSuggestionList.some((s) => s.id === clickedSuggestion.id);

    // Don't force another LB card while the user is reviewing one already in the set
    // (including Accepted / just-built suggestions).
    if (clickedStillInList) {
      return;
    }

    // User cleared selection by switching to a tab with no pending suggestion.
    if (!shouldKitAutoSelect && clickedSuggestion == null) {
      return;
    }

    // Only auto-select within Load Building. Never steal selection when the
    // user explicitly chose another pipeline (e.g. Quick Quote).
    const shouldSelectLb =
      clickedSuggestion == null ||
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding;

    if (!shouldSelectLb) {
      return;
    }

    if (clickedSuggestion?.id === firstLb.id) {
      return;
    }

    void goToSuggestion({
      suggestionID: firstLb.id,
    });
  }, [
    curSuggestionList,
    clickedSuggestion,
    goToSuggestion,
    setCurrentState,
    shouldKitAutoSelect,
  ]);

  // "opened" means this tab became visible with a load-building suggestion
  // selected. LoadBuildingTab sits inside a Radix TabsContent without
  // forceMount, so it mounts only while that tab is shown. Depending on the id
  // rather than the suggestion object reports once per suggestion per visit;
  // the backend keeps the first timestamp and appends later opens to history.
  const openedSuggestionId = shouldRecordLoadBuildingSuggestionOpened(
    clickedSuggestion
  )
    ? clickedSuggestion.id
    : null;
  useEffect(() => {
    if (!openedSuggestionId) {
      return;
    }

    // Fire-and-forget: recordSuggestionOpened never rejects.
    void recordSuggestionOpened(openedSuggestionId);
  }, [openedSuggestionId]);

  useEffect(() => {
    const clearTmsFlags = () => {
      setIsMcleodEnterpriseTMS(null);
      setIsTurvoTMS(null);
      setIsTaiTMS(null);
      setIsRevenovaTMS(null);
      setIsAljexTMS(null);
      setIsGlobalTranzTMS(null);
      setIsThreeGTMS(null);
      setIsStarkTMS(null);
      setIsMercurygateBoomiTMS(null);
      setIsPlsTMS(null);
    };

    clearTmsFlags();

    if (!tmsIntegrations?.length) {
      return;
    }

    // Prefer a known create-form TMS when multiple integrations exist (e.g. PLS
    // staging+prod). Fall back to [0] for single-TMS services.
    const primaryTMS =
      tmsIntegrations.find((integration) => integration.name === TMS.PLS) ??
      tmsIntegrations.find(
        (integration) => !integration.isTestingEnvironment
      ) ??
      tmsIntegrations[0];

    if (tmsIntegrations.length > 1 && isProd()) {
      const distinctNames = new Set(tmsIntegrations.map((t) => t.name));
      if (distinctNames.size > 1) {
        captureException(
          new Error(
            'Service with multiple TMS integrations trying to build load'
          ),
          {
            serviceID: serviceID,
            tmsIntegrations: tmsIntegrations,
          }
        );
      }
    }

    switch (primaryTMS.name) {
      case TMS.McleodEnterprise:
      case TMS.Mcleod:
        setIsMcleodEnterpriseTMS(true);
        break;
      case TMS.Turvo:
        setIsTurvoTMS(true);
        break;
      case TMS.Tai:
        setIsTaiTMS(true);
        break;
      case TMS.Revenova:
        setIsRevenovaTMS(true);
        break;
      case TMS.Aljex:
        setIsAljexTMS(true);
        break;
      case TMS.GlobalTranzTMS:
        setIsGlobalTranzTMS(true);
        break;
      case TMS.ThreeG:
        setIsThreeGTMS(true);
        break;
      case TMS.Stark:
        setIsStarkTMS(true);
        break;
      case TMS.MercurygateBoomi:
        setIsMercurygateBoomiTMS(true);
        break;
      case TMS.PLS:
        setIsPlsTMS(true);
        break;
      default:
        break;
    }
  }, [serviceID, tmsIntegrations]);

  const mcleodIntegration = getMcleodIntegrationFromList(tmsIntegrations);
  const plsCatalogTMS =
    tmsIntegrations?.find(
      (integration) =>
        integration.name === TMS.PLS && !integration.isTestingEnvironment
    ) ?? tmsIntegrations?.find((integration) => integration.name === TMS.PLS);

  let activeTMS = tmsIntegrations?.[0];
  if (isMcleodEnterpriseTMS === true && mcleodIntegration) {
    activeTMS = mcleodIntegration;
  } else if (isPlsTMS === true && plsCatalogTMS) {
    activeTMS = plsCatalogTMS;
  }

  const tmsFlags: LoadBuildingTmsFlags = {
    isMcleodEnterpriseTMS,
    isTurvoTMS,
    isTaiTMS,
    isRevenovaTMS,
    isAljexTMS,
    isGlobalTranzTMS,
    isThreeGTMS,
    isStarkTMS,
    isMercurygateBoomiTMS,
    isPlsTMS,
  };

  if (isServiceLoading) {
    return (
      <Flex className='px-4 py-8' justify='center' align='center'>
        <Typography variant='body-sm' className='text-neutral-600'>
          Loading service...
        </Typography>
      </Flex>
    );
  }

  if (!activeTMS || !isLoadBuildingFormReady(tmsFlags)) {
    return null;
  }

  // Every card on the thread is built and none is open or selected: show the built loads, not an
  // empty form. A card built in this session stays selected, so its form keeps the created banner.
  const builtKey = builtSuggestions.map(({ id }) => id).join(',');
  const hasLoadBuildingCard =
    clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding ||
    curSuggestionList.some(
      (s) => s.pipeline === SuggestionPipelines.LoadBuilding
    );
  if (
    onOpenBuiltLoad &&
    builtSuggestions.length > 0 &&
    !hasLoadBuildingCard &&
    buildAnotherForKey !== builtKey
  ) {
    return (
      <BuiltLoadsNotice
        suggestions={builtSuggestions}
        onOpenLoad={onOpenBuiltLoad}
        onBuildAnother={() => setBuildAnotherForKey(builtKey)}
      />
    );
  }

  const isTestingEnvironment = activeTMS.isTestingEnvironment ?? false;
  const tmsDisplayName =
    integrationNameMap[activeTMS.name as keyof typeof integrationNameMap] ??
    activeTMS.name;

  return (
    <LoadBuildingGuardrailProvider
      isTestingEnvironment={isTestingEnvironment}
      tmsName={tmsDisplayName}
    >
      <TMSProvider
        tmsID={activeTMS.id}
        tmsName={activeTMS.name}
        tenant={activeTMS.tenant}
        isTestingEnvironment={isTestingEnvironment}
      >
        <LoadBuildingFormContent
          isMcleodEnterpriseTMS={isMcleodEnterpriseTMS}
          isTurvoTMS={isTurvoTMS}
          isTaiTMS={isTaiTMS}
          isRevenovaTMS={isRevenovaTMS}
          isAljexTMS={isAljexTMS}
          isGlobalTranzTMS={isGlobalTranzTMS}
          isThreeGTMS={isThreeGTMS}
          isStarkTMS={isStarkTMS}
          isMercurygateBoomiTMS={isMercurygateBoomiTMS}
          isPlsTMS={isPlsTMS}
        />
        <LoadBuildingGuardrailBanner />
      </TMSProvider>
    </LoadBuildingGuardrailProvider>
  );
}

type LoadBuildingFormContentProps = LoadBuildingTmsFlags;

function LoadBuildingFormContent({
  isMcleodEnterpriseTMS,
  isTurvoTMS,
  isTaiTMS,
  isRevenovaTMS,
  isAljexTMS,
  isGlobalTranzTMS,
  isThreeGTMS,
  isStarkTMS,
  isMercurygateBoomiTMS,
  isPlsTMS,
}: LoadBuildingFormContentProps) {
  switch (true) {
    case isMcleodEnterpriseTMS:
      return <UnifiedMcLeodLoadForm isCreateMode={true} />;
    case isTurvoTMS:
      return <TurvoLoadForm isCreateMode={true} />;
    case isTaiTMS:
      return <UnifiedTaiLoadForm isCreateMode={true} />;
    case isRevenovaTMS:
      return <UnifiedRevenovaLoadForm isCreateMode={true} />;
    case isAljexTMS:
      return <UnifiedAljexLoadForm isCreateMode={true} />;
    case isGlobalTranzTMS:
      return <UnifiedGlobalTranzForm isCreateMode={true} />;
    case isThreeGTMS:
      return <UnifiedThreeGLoadForm isCreateMode={true} />;
    case isStarkTMS:
      return <UnifiedStarkLoadForm isCreateMode={true} />;
    case isMercurygateBoomiTMS:
      return <UnifiedMercuryGateBoomiLoadForm isCreateMode={true} />;
    case isPlsTMS:
      return <UnifiedPlsLoadForm isCreateMode={true} />;
    default:
      return null;
  }
}

function LoadBuildingGuardrailBanner() {
  const { showBanner, tmsName } = useLoadBuildingGuardrail();
  if (!showBanner) {
    return null;
  }
  return <ProdTMSWarningBanner tmsName={tmsName} />;
}
